import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { EMPTY } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { WorkOrderService } from '../../workorder/services/workorder.service';
import { DealerState } from '../../../shared/state/DealerState';
import { BreadcrumbContextService } from '../../../shared/services/breadcrumb-context.service';
import { AlertService, AlertType, Alert } from '@michelin/theme';
import { CasingSearchResult } from '../model/casing-search-result.model';
import { WorkOrderFiltersModel } from '../../workorder/components/model/work-order-filters.model';
import { Dealer } from '../../../shared/dealer/models/dealer';
import { isWorkOrderLineEditable } from '../../workorder/shared/util/work-order-helper-util';

@Injectable({
  providedIn: 'root'
})
export class EnhancedCasingNavigationService {
  public router = inject(Router);
  public store = inject(Store);
  public workOrderService = inject(WorkOrderService);
  public breadcrumbContextService = inject(BreadcrumbContextService);
  public alertService = inject(AlertService);

  /**
   * Navigate to WorkOrderDetailsComponent
   * Simply calls service and navigates - state management is handled internally
   */
  navigateToWorkOrderDetails(casingResult: CasingSearchResult): void {
    if (!this.validateWorkOrderData(casingResult, ['workOrderHeaderPk', 'workOrderNum'])) {
      return;
    }

    const dealerId = this.getDealerId();
    if (!dealerId) return;

    this.setBreadcrumbContext();

    const filters = this.createWorkOrderFilters(casingResult.workOrderHeaderPk);

    this.workOrderService
      .searchWorkOrders(dealerId, filters, 1, 10, 'workOrderHeaderPk:ASC')
      .pipe(
        tap((response) => {
          if (this.isValidResponse(response, 'workorders')) {
            // Mark as programmatic navigation to preserve casing search context
            this.breadcrumbContextService.setProgrammaticNavigation(true);
            this.router.navigate(['/home/work-orders/work-orders-details', casingResult.workOrderHeaderPk]);
          } else {
            this.showNavigationError('Work order not found');
          }
        }),
        catchError((error) => this.handleNavigationError(error, 'work order details'))
      )
      .subscribe();
  }

  /**
   * Navigate to WorkOrderLineDetailsComponent
   * Uses service composition - all state updates handled by services
   */
  navigateToWorkOrderLineDetails(casingResult: CasingSearchResult): void {
    if (!this.validateWorkOrderData(casingResult, ['workOrderHeaderPk', 'workOrderLinePk', 'casingId'])) {
      return;
    }

    const dealerId = this.getDealerId();
    if (!dealerId) return;

    this.setBreadcrumbContext();

    const filters = this.createWorkOrderFilters(casingResult.workOrderHeaderPk);

    // Step 1: Load work orders (service handles state)
    this.workOrderService
      .searchWorkOrders(dealerId, filters, 1, 2, 'workOrderHeaderPk:ASC')
      .pipe(
        tap((workOrderResponse) => {
          if (!this.isValidResponse(workOrderResponse, 'workorders')) {
            throw new Error('Work order not found');
          }
        }),
        // Step 2: Load specific work order line using filtering
        switchMap(() =>
          this.workOrderService.searchWorkOrderLines(dealerId, casingResult.workOrderHeaderPk, 1, 1, casingResult.workOrderLinePk)
        ),
        tap((workOrderLineResponse) => {
          if (!this.isValidResponse(workOrderLineResponse, 'workOrderLines')) {
            throw new Error('Work order lines not found');
          }
          // Verify that our specific work order line is in the response
          const targetLine = workOrderLineResponse.data.workOrderLines.find((line) => line.id === casingResult.workOrderLinePk);
          if (!targetLine) {
            throw new Error('Target work order line not found in response');
          }
        }),
        // Navigate immediately after service completes (store state is already updated)
        tap(() => {
          this.breadcrumbContextService.setProgrammaticNavigation(true);
          this.router.navigate(
            ['/home/work-orders/work-order-line-details', casingResult.workOrderHeaderPk, casingResult.workOrderLinePk],
            {
              queryParams: { isEditable: isWorkOrderLineEditable(casingResult.casingStatus, casingResult.lockPost) }
            }
          );
        }),
        catchError((error) => this.handleNavigationError(error, 'work order line details'))
      )
      .subscribe();
  }

  // --- Helper Methods ---

  private validateWorkOrderData(casingResult: CasingSearchResult, requiredFields: string[]): boolean {
    for (const field of requiredFields) {
      if (!casingResult[field as keyof CasingSearchResult]) {
        this.showNavigationError(`Invalid work order information: missing ${field}`);
        return false;
      }
    }
    return true;
  }

  private getDealerId(): string | null {
    const dealerId = (this.store.selectSnapshot(DealerState.currentDealer) as Dealer)?.id;
    if (!dealerId) {
      this.showNavigationError('No dealer selected');
      return null;
    }
    return dealerId;
  }

  private setBreadcrumbContext(): void {
    this.breadcrumbContextService.setNavigationContext({
      source: 'casing-search',
      breadcrumbLabel: 'Casing List'
    });
  }

  private createWorkOrderFilters(workOrderHeaderPk?: string): WorkOrderFiltersModel {
    return {
      workOrderNumber: '',
      workOrderHeaderPk: workOrderHeaderPk || '',
      workOrderStatus: [],
      workOrderPlant: [],
      workOrderSite: [],
      customerCode: '',
      customerName: '',
      crn: '',
      workOrderReceivedDateStart: '',
      workOrderReceivedDateEnd: '',
      workOrderDueDateStart: '',
      workOrderDueDateEnd: '',
      searchByUser: ''
    };
  }

  private isValidResponse(response: any, dataProperty: string): boolean {
    return response && response.data && response.data[dataProperty] && response.data[dataProperty].length > 0;
  }

  private handleNavigationError(error: any, context: string) {
    console.error('Navigation error:', error);
    this.showNavigationError(`Failed to load ${context}`);
    return EMPTY;
  }

  private showNavigationError(message: string): void {
    this.alertService.showAlert({
      type: AlertType.strong,
      content: `Navigation failed: ${message}`
    } as Alert);
  }
}
